import { gsap } from "gsap";

// Referência ao backdrop global
let backdrop = null;
let activeModal = null;

/**
 * Inicializa todos os modais baseados em classes
 * Funciona com data-target para vincular o trigger ao modal
 */
export function initFloatingModals() {
  // Pega o backdrop global do projeto
  backdrop = document.getElementById("backdrop");

  const triggers = document.querySelectorAll("[data-modal-target]");

  triggers.forEach((trigger) => {
    initSingleModal(trigger);
  });

  // Fecha modais ao clicar no botão de fechar
  document.querySelectorAll("[data-modal-close]").forEach((closeBtn) => {
    closeBtn.addEventListener("click", () => {
      const modal = closeBtn.closest(".modal");
      if (modal) {
        closeModal(modal);
      }
    });
  });

  // Transição entre modais (fecha atual e abre outro)
  document
    .querySelectorAll("[data-modal-close-current][data-modal-target]")
    .forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.preventDefault();
        const currentModal = btn.closest(".modal");
        const targetSelector = btn.getAttribute("data-modal-target");
        const nextModal = document.querySelector(targetSelector);

        if (currentModal && nextModal) {
          // Fecha o modal atual sem esconder o backdrop
          switchModal(currentModal, nextModal);
        }
      });
    });

  // Fecha modal ao clicar no backdrop
  if (backdrop) {
    backdrop.addEventListener("click", () => {
      if (activeModal) {
        closeModal(activeModal);
      }
    });
  }
}

/**
 * Inicializa um único modal
 *
 * @param {HTMLElement} triggerElement - Elemento trigger do modal
 */
function initSingleModal(triggerElement) {
  const targetSelector = triggerElement.getAttribute("data-modal-target");
  if (!targetSelector) return;

  const modalElement = document.querySelector(targetSelector);
  if (!modalElement) return;

  const modalContent = modalElement.querySelector(".modal-content");

  triggerElement.addEventListener("click", (e) => {
    e.preventDefault();
    openModal(modalElement);
  });

  // Fecha ao clicar fora do conteúdo do modal (na área escura)
  modalElement.addEventListener("click", (e) => {
    // Se clicou diretamente no modal (não no conteúdo), fecha
    if (e.target === modalElement) {
      closeModal(modalElement);
    }
  });

  // Impede que cliques no conteúdo fechem o modal
  if (modalContent) {
    modalContent.addEventListener("click", (e) => {
      e.stopPropagation();
    });
  }

  // Fecha ao pressionar ESC
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && modalElement.classList.contains("active")) {
      closeModal(modalElement);
    }
  });
}

/**
 * Abre um modal
 *
 * @param {HTMLElement} modalElement - Elemento do modal
 */
export function openModal(modalElement) {
  const modalContent = modalElement.querySelector(".modal-content");

  // Previne scroll do body
  document.body.style.overflow = "hidden";

  // Guarda referência do modal ativo
  activeModal = modalElement;

  // Exibe o modal e o backdrop
  modalElement.style.display = "flex";
  modalElement.classList.add("active");

  // Animação do backdrop - mata animações pendentes antes
  if (backdrop) {
    gsap.killTweensOf(backdrop);
    gsap.set(backdrop, { display: "block" });
    gsap.to(backdrop, {
      opacity: 1,
      pointerEvents: "auto",
      duration: 0.3,
      ease: "power2.out",
    });
  }

  // Animação do conteúdo do modal - mata animações pendentes antes
  gsap.killTweensOf(modalContent);
  gsap.fromTo(
    modalContent,
    { opacity: 0, scale: 0.95, y: 20 },
    { opacity: 1, scale: 1, y: 0, duration: 0.25, ease: "power2.out" }
  );
}

/**
 * Fecha um modal
 *
 * @param {HTMLElement} modalElement - Elemento do modal
 */
export function closeModal(modalElement) {
  const modalContent = modalElement.querySelector(".modal-content");

  modalElement.classList.remove("active");
  activeModal = null;

  // Animação do backdrop - mata animações pendentes e só esconde se não houver outro modal ativo
  if (backdrop) {
    gsap.killTweensOf(backdrop);
    gsap.to(backdrop, {
      opacity: 0,
      pointerEvents: "none",
      duration: 0.2,
      ease: "power2.in",
      onComplete: () => {
        // Só esconde o backdrop se não houver modal ativo
        if (!activeModal) {
          gsap.set(backdrop, { display: "none" });
        }
      },
    });
  }

  // Animação do conteúdo do modal - mata animações pendentes antes
  gsap.killTweensOf(modalContent);
  gsap.to(modalContent, {
    opacity: 0,
    scale: 0.95,
    y: 20,
    duration: 0.15,
    ease: "power2.in",
    onComplete: () => {
      modalElement.style.display = "none";
      // Restaura scroll do body apenas se não houver modal ativo
      if (!activeModal) {
        document.body.style.overflow = "";
      }
    },
  });
}

/**
 * Troca de um modal para outro (sem fechar o backdrop)
 *
 * @param {HTMLElement} currentModal - Modal atual
 * @param {HTMLElement} nextModal - Próximo modal
 */
export function switchModal(currentModal, nextModal) {
  const currentContent = currentModal.querySelector(".modal-content");
  const nextContent = nextModal.querySelector(".modal-content");

  // Fecha o modal atual
  currentModal.classList.remove("active");

  // Mata animações pendentes
  gsap.killTweensOf(currentContent);
  gsap.killTweensOf(nextContent);

  gsap.to(currentContent, {
    opacity: 0,
    scale: 0.95,
    y: 20,
    duration: 0.15,
    ease: "power2.in",
    onComplete: () => {
      currentModal.style.display = "none";

      // Abre o próximo modal
      activeModal = nextModal;
      nextModal.style.display = "flex";
      nextModal.classList.add("active");

      gsap.fromTo(
        nextContent,
        { opacity: 0, scale: 0.95, y: 20 },
        { opacity: 1, scale: 1, y: 0, duration: 0.25, ease: "power2.out" }
      );
    },
  });
}

/**
 * Abre um modal pelo ID
 *
 * @param {string} modalId - ID do modal (ex: "#modal-address")
 */
export function openModalById(modalId) {
  const modalElement = document.querySelector(modalId);
  if (modalElement) {
    openModal(modalElement);
  }
}

/**
 * Fecha um modal pelo ID
 *
 * @param {string} modalId - ID do modal (ex: "#modal-address")
 */
export function closeModalById(modalId) {
  const modalElement = document.querySelector(modalId);
  if (modalElement) {
    closeModal(modalElement);
  }
}
